import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Checkbox,
  DatePicker,
  Divider,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  TimePicker,
  Tag,
} from "antd";
import moment from "moment";
import "moment-timezone";
import { useDispatch } from "react-redux";
import PageBreadcrumb from "../../component/PageBreadcrumb";
import {
  CreateOrEditAiPastSession,
  DeleteAiPastSession,
  ListAiPastSessions,
} from "../../redux/actions/AiPastSessionsAction";
import {
  AssignAiSessionsToBatch,
  ListAiBatches,
} from "../../redux/actions/AiSessionsAction";

const dateFormat = "YYYY-MM-DD";
const PAGE_SIZE = 10;
const IST_TZ = "Asia/Kolkata";

const getId = (item) => item?._id ?? item?.id;

const getBatchLabel = (batch) =>
  batch?.name ??
  batch?.batchName ??
  batch?.title ??
  batch?.batchTitle ??
  batch?.code ??
  batch?.slug ??
  getId(batch);

const TIME_FORMATS = ["HH:mm", "HH:mm:ss", "h:mm A"];

const formatIstTime = (timeValue) => {
  if (!timeValue || timeValue === "-") return null;
  const parsed = moment(String(timeValue).trim(), TIME_FORMATS, true);
  if (!parsed.isValid()) return null;
  return parsed.format("HH:mm");
};

const toIstTimePayload = (time) => {
  if (!time) return undefined;
  return moment(time).format("HH:mm");
};

const toIstDatePayload = (date) => {
  if (!date) return undefined;
  return moment(date).format(dateFormat);
};

const formatIstDate = (dateValue) => {
  if (!dateValue || dateValue === "-") return null;
  const raw = String(dateValue).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const parsed = moment.utc(raw);
  if (!parsed.isValid()) {
    const local = moment(raw);
    return local.isValid() ? local.format(dateFormat) : null;
  }
  return parsed.tz(IST_TZ).format(dateFormat);
};

const toUtcIso = (date, time) => {
  if (!date || !time) return null;
  const dateStr = moment(date).format(dateFormat);
  const timeStr = moment(time).format("HH:mm");
  return moment
    .tz(`${dateStr} ${timeStr}`, `${dateFormat} HH:mm`, IST_TZ)
    .utc()
    .toISOString();
};

const istNow = () => moment.tz(IST_TZ);

const istNowPickerTime = () => moment(istNow().format("HH:mm"), "HH:mm");

const istTodayPickerDate = () => moment(istNow().format(dateFormat), dateFormat);

const IstDatePicker = ({ value, onChange, ...rest }) => {
  const [open, setOpen] = useState(false);
  const handleChange = (val) => {
    onChange?.(val);
    setOpen(false);
  };
  return (
    <DatePicker
      {...rest}
      value={value}
      open={open}
      onOpenChange={setOpen}
      onChange={handleChange}
      showToday={false}
      renderExtraFooter={() => (
        <a
          onClick={(e) => {
            e.preventDefault();
            handleChange(istTodayPickerDate());
          }}
        >
          Today
        </a>
      )}
    />
  );
};

const IstTimePicker = ({ value, onChange, ...rest }) => {
  const [open, setOpen] = useState(false);
  return (
    <TimePicker
      {...rest}
      value={value}
      open={open}
      onOpenChange={setOpen}
      onChange={onChange}
      showNow={false}
      renderExtraFooter={() => (
        <a
          onClick={(e) => {
            e.preventDefault();
            onChange?.(istNowPickerTime());
            setOpen(false);
          }}
        >
          Now
        </a>
      )}
    />
  );
};

const normalizeNotesPdfUrl = (notesPdfUrl) => {
  if (!notesPdfUrl) return [];
  if (Array.isArray(notesPdfUrl)) return notesPdfUrl.filter(Boolean);
  if (typeof notesPdfUrl === "string") {
    // Allow comma-separated value or a single URL
    const trimmed = notesPdfUrl.trim();
    if (!trimmed) return [];
    if (trimmed.includes(",")) {
      return trimmed
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    }
    return [trimmed];
  }
  return [String(notesPdfUrl)];
};

const urlsToNotesItems = (urls) => {
  const safe = Array.isArray(urls) ? urls.filter(Boolean) : [];
  if (!safe.length) return [{ url: "", enabled: true }];
  return safe.map((u) => ({ url: u, enabled: true }));
};

const AiPastSessions = () => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [batches, setBatches] = useState([]);
  const [batchesLoading, setBatchesLoading] = useState(false);

  const fetchSessions = useCallback(
    (page = 1) => {
      setLoading(true);
      dispatch(
        ListAiPastSessions(
          { page, limit: PAGE_SIZE },
          ({ list, pagination }) => {
            const safeList = Array.isArray(list) ? list : [];
            setSessions(safeList);
            const apiTotal =
              pagination?.totalItems ?? pagination?.total ?? safeList.length;
            setTotalItems(apiTotal);
            setCurrentPage(page);
            setLoading(false);
          },
          (errMsg) => {
            setLoading(false);
            Modal.error({
              title: "Failed to fetch sessions",
              content: errMsg,
            });
          }
        )
      );
    },
    [dispatch]
  );

  const fetchBatches = useCallback(() => {
    setBatchesLoading(true);
    dispatch(
      ListAiBatches(
        { page: 1, limit: 50, status: "active" },
        ({ list }) => {
          setBatches(Array.isArray(list) ? list : []);
          setBatchesLoading(false);
        },
        (errMsg) => {
          setBatchesLoading(false);
          Modal.error({
            title: "Failed to fetch batches",
            content: errMsg,
          });
        }
      )
    );
  }, [dispatch]);

  useEffect(() => {
    moment.tz.setDefault(IST_TZ);
    fetchSessions(1);
    fetchBatches();
    return () => {
      moment.tz.setDefault();
    };
  }, [fetchSessions, fetchBatches]);

  const resetToCreate = () => {
    setEditingId(null);
    form.resetFields();
    form.setFieldsValue({
      status: "upcoming",
      notesPdfUrl: [{ url: "", enabled: true }],
      batchIds: [],
      notificationChannels: ["whatsapp", "email"],
      isQnAEnabled: true,
      isVotingEnabled: true,
    });
  };

  const startEdit = (record) => {
    setEditingId(record?._id ?? null);
    fetchBatches();
    const notes = normalizeNotesPdfUrl(record?.notesPdfUrl);
    const istTime = formatIstTime(record?.originalTime ?? record?.time);
    const istDate = formatIstDate(record?.date);
    form.setFieldsValue({
      title: record?.title ?? "",
      date: istDate ? moment(istDate, dateFormat) : null,
      time: istTime ? moment(istTime, "HH:mm") : null,
      instructor: record?.instructor ?? "",
      status: record?.status ?? "upcoming",
      description: record?.description ?? "",
      videoUrl: record?.videoUrl ?? "",
      zoomMeetingLink: record?.zoomMeetingLink ?? "",
      notesPdfUrl: urlsToNotesItems(notes),
      batchIds: [],
      notificationChannels: ["whatsapp", "email"],
      isQnAEnabled: record?.isQnAEnabled !== false,
      isVotingEnabled: record?.isVotingEnabled !== false,
    });
  };

  const tableData = useMemo(() => {
    const offset = (currentPage - 1) * PAGE_SIZE;
    return (sessions ?? []).map((s, i) => {
      const id = s?._id ?? s?.id ?? String(i);

      const formattedDate = formatIstDate(s?.date) ?? (s?.date ? String(s.date) : "-");
      const istTime = s?.time ?? s?.sessionTime ?? s?.startTime;
      const formattedTime = formatIstTime(istTime) ?? istTime ?? "-";

      return {
        key: id,
        _id: id,
        slNo: offset + i + 1,
        title: s?.title ?? "-",
        date: formattedDate,
        originalTime: istTime,
        time: formattedTime,
        instructor: s?.instructor ?? s?.teacher ?? "-",
        status: s?.status ?? s?.sessionStatus ?? "-",
        description: s?.description ?? "-",
        videoUrl: s?.videoUrl ?? s?.videoURL ?? "-",
        zoomMeetingLink: s?.zoomMeetingLink ?? "-",
        notesPdfUrl: normalizeNotesPdfUrl(s?.notesPdfUrl),
        isQnAEnabled: s?.isQnAEnabled !== false,
        isVotingEnabled: s?.isVotingEnabled !== false,
      };
    });
  }, [sessions, currentPage]);

  const columns = [
      {
        title: "Sl No",
        width: 70,
        dataIndex: "slNo",
        key: "slNo",
      },
      {
        title: "Title",
        width: 220,
        dataIndex: "title",
        key: "title",
      },
      {
        title: "Date",
        width: 120,
        dataIndex: "date",
        key: "date",
      },
      {
        title: "Time (IST)",
        width: 110,
        dataIndex: "time",
        key: "time",
      },
      {
        title: "Instructor",
        width: 180,
        dataIndex: "instructor",
        key: "instructor",
      },
      {
        title: "Status",
        width: 130,
        dataIndex: "status",
        key: "status",
        render: (status) => {
          const normalized = String(status ?? "").toLowerCase();
          const color =
            normalized === "upcoming"
              ? "blue"
              : normalized === "onprogress"
              ? "gold"
              : normalized === "published"
              ? "green"
              : "default";
          return <Tag color={color}>{status || "-"}</Tag>;
        },
      },
      {
        title: "Video",
        width: 180,
        dataIndex: "videoUrl",
        key: "videoUrl",
        render: (url) =>
          url && url !== "-" ? (
            <a href={url} target="_blank" rel="noreferrer">
              Open
            </a>
          ) : (
            "-"
          ),
      },
      {
        title: "Zoom Link",
        width: 180,
        dataIndex: "zoomMeetingLink",
        key: "zoomMeetingLink",
        render: (url) =>
          url && url !== "-" ? (
            <a href={url} target="_blank" rel="noreferrer">
              Open
            </a>
          ) : (
            "-"
          ),
      },
      {
        title: "Notes PDFs",
        width: 220,
        dataIndex: "notesPdfUrl",
        key: "notesPdfUrl",
        render: (urls) =>
          urls?.length ? (
            <Space direction="vertical" size={4}>
              {urls.slice(0, 3).map((u, idx) => (
                <a key={`${u}-${idx}`} href={u} target="_blank" rel="noreferrer">
                  Notes {idx + 1}
                </a>
              ))}
              {urls.length > 3 ? <span>+{urls.length - 3} more</span> : null}
            </Space>
          ) : (
            "-"
          ),
      },
      {
        title: "Q&A",
        width: 90,
        dataIndex: "isQnAEnabled",
        key: "isQnAEnabled",
        render: (enabled) =>
          enabled ? <Tag color="green">On</Tag> : <Tag>Off</Tag>,
      },
      {
        title: "Voting",
        width: 90,
        dataIndex: "isVotingEnabled",
        key: "isVotingEnabled",
        render: (enabled) =>
          enabled ? <Tag color="green">On</Tag> : <Tag>Off</Tag>,
      },
      {
        title: "Description",
        width: 260,
        dataIndex: "description",
        key: "description",
        render: (desc) => <span title={desc}>{desc}</span>,
      },
      {
        title: "Action",
        key: "action",
        fixed: "right",
        width: 120,
        render: (_, record) => (
          <Space size={8}>
            <Button
              onClick={() => {
                startEdit(record);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
            >
              Edit
            </Button>
            <Button
              danger
              onClick={() => {
                Modal.confirm({
                  title: "Delete this AI past session?",
                  content: "This action cannot be undone.",
                  okText: "Delete",
                  cancelText: "Cancel",
                  onOk: () => {
                    dispatch(
                      DeleteAiPastSession(
                        record._id,
                        () => {
                          Modal.success({
                            title: "Deleted successfully",
                          });
                          // If you were editing this record, exit edit mode
                          if (editingId === record._id) resetToCreate();
                          fetchSessions(currentPage);
                        },
                        (errMsg) => {
                          Modal.error({
                            title: "Delete failed",
                            content: errMsg,
                          });
                        }
                      )
                    );
                  },
                });
              }}
            >
              Delete
            </Button>
          </Space>
        ),
      },
  ];

  const assignSessionToBatch = (batch, sessionId, values) =>
    new Promise((resolve, reject) => {
      const batchId = getId(batch);
      const existingSessions = Array.isArray(batch?.sessions)
        ? batch.sessions
        : [];
      if (
        existingSessions.some(
          (session) => String(session?.sessionId) === String(sessionId)
        )
      ) {
        resolve();
        return;
      }
      const nextOrder =
        existingSessions.reduce(
          (max, session) => Math.max(max, Number(session?.order) || 0),
          0
        ) + 1;

      dispatch(
        AssignAiSessionsToBatch(
          {
            batchId,
            sessions: [
              ...existingSessions.map((session) => ({
                sessionId: session?.sessionId,
                title: session?.title,
                order: session?.order,
                releaseAt: session?.releaseAt,
              })),
              {
                sessionId,
                title: values?.title,
                order: nextOrder,
                releaseAt: toUtcIso(values?.date, values?.time),
              },
            ],
            notificationChannels: values?.notificationChannels ?? [],
          },
          resolve,
          (errMsg) => reject(new Error(errMsg))
        )
      );
    });

  const extractSavedSessionId = (responseBody) => {
    const inner = responseBody?.data ?? responseBody;
    return (
      getId(inner) ??
      getId(inner?.session) ??
      getId(inner?.result) ??
      getId(responseBody?.session) ??
      null
    );
  };

  const batchOptions = useMemo(() => {
    return (batches ?? []).map((batch) => {
      const id = String(getId(batch));
      const alreadyAssigned = Boolean(
        editingId &&
          Array.isArray(batch?.sessions) &&
          batch.sessions.some(
            (session) => String(session?.sessionId) === String(editingId)
          )
      );
      const sessionsCount = Array.isArray(batch?.sessions)
        ? batch.sessions.length
        : 0;
      return {
        label: alreadyAssigned
          ? `${getBatchLabel(batch)} (Already assigned)`
          : `${getBatchLabel(batch)} (${sessionsCount} session${
              sessionsCount === 1 ? "" : "s"
            })`,
        value: id,
        disabled: alreadyAssigned,
      };
    });
  }, [batches, editingId]);

  const selectableBatchIds = useMemo(
    () => batchOptions.filter((o) => !o.disabled).map((o) => o.value),
    [batchOptions]
  );

  const onFinish = async (values) => {
    setSubmitting(true);

    const payload = {
      ...(editingId ? { id: editingId } : {}),
      title: values?.title,
      date: toIstDatePayload(values?.date),
      time: toIstTimePayload(values?.time),
      instructor: values?.instructor,
      status: values?.status,
      description: values?.description,
      videoUrl: values?.videoUrl,
      zoomMeetingLink: values?.zoomMeetingLink,
      notesPdfUrl: (values?.notesPdfUrl ?? [])
        .filter((item) => item?.enabled)
        .map((item) => item?.url)
        .filter(Boolean),
      isQnAEnabled: values?.isQnAEnabled !== false,
      isVotingEnabled: values?.isVotingEnabled !== false,
    };

    try {
      const responseBody = await new Promise((resolve, reject) => {
        dispatch(
          CreateOrEditAiPastSession(
            payload,
            resolve,
            (errMsg) => reject(new Error(errMsg))
          )
        );
      });

      const savedSessionId = editingId ?? extractSavedSessionId(responseBody);

      const selectedBatchIds = values?.batchIds ?? [];
      const selectedBatches = batches.filter((batch) =>
        selectedBatchIds.includes(String(getId(batch)))
      );

      if (selectedBatches.length && !savedSessionId) {
        Modal.warning({
          title: "Session saved, but could not add to batches",
          content:
            "The session id was not returned by the server. Please edit the session and add it to batches from there.",
        });
      } else if (selectedBatches.length) {
        await Promise.all(
          selectedBatches.map((batch) =>
            assignSessionToBatch(batch, savedSessionId, values)
          )
        );
      }

      const assignedToBatches = Boolean(
        selectedBatches.length && savedSessionId
      );
      resetToCreate();
      fetchSessions(editingId ? currentPage : 1);
      fetchBatches();
      Modal.success({
        title: editingId
          ? assignedToBatches
            ? "Session updated and added to batches"
            : "Session updated successfully"
          : assignedToBatches
          ? "Session saved and added to batches"
          : "Session saved successfully",
      });
    } catch (error) {
      Modal.error({
        title: "Save failed",
        content: error?.message || "Something went wrong",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <PageBreadcrumb route={["dashboard", "AI Past Sessions"]} />

      <Card title="Add AI Past Session" style={{ marginBottom: "1rem" }}>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{
            status: "upcoming",
            notesPdfUrl: [{ url: "", enabled: true }],
            batchIds: [],
            notificationChannels: ["whatsapp", "email"],
            isQnAEnabled: true,
            isVotingEnabled: true,
          }}
        >
          {editingId ? (
            <div style={{ marginBottom: 12 }}>
              <Tag color="purple">Editing Mode</Tag>
              <Button
                style={{ marginLeft: 12 }}
                onClick={() => resetToCreate()}
              >
                Cancel
              </Button>
            </div>
          ) : null}
          <Space
            size={16}
            style={{ display: "flex", flexWrap: "wrap" }}
            align="start"
          >
            <Form.Item
              label="Title"
              name="title"
              style={{ flex: "1 1 260px" }}
              rules={[{ required: true, message: "Please enter title" }]}
            >
              <Input placeholder="e.g. Introduction to Midjourney" />
            </Form.Item>

            <Form.Item
              label="Date"
              name="date"
              style={{ flex: "1 1 180px" }}
              rules={[{ required: true, message: "Please select date" }]}
            >
              <IstDatePicker format={dateFormat} />
            </Form.Item>

            <Form.Item
              label="Time (IST)"
              name="time"
              style={{ flex: "1 1 180px" }}
              rules={[{ required: true, message: "Please select time" }]}
            >
              <IstTimePicker format="HH:mm" minuteStep={5} />
            </Form.Item>

            <Form.Item
              label="Instructor"
              name="instructor"
              style={{ flex: "1 1 240px" }}
              rules={[{ required: true, message: "Please enter instructor" }]}
            >
              <Input placeholder="e.g. Rishabh Jain" />
            </Form.Item>

            <Form.Item
              label="Status"
              name="status"
              style={{ flex: "1 1 180px" }}
              rules={[{ required: true, message: "Please select status" }]}
            >
              <Select
                options={[
                  { label: "Published", value: "published" },
                  { label: "On Progress", value: "onprogress" },
                  { label: "Upcoming", value: "upcoming" },
                ]}
              />
            </Form.Item>

            <Form.Item
              label="Q&A"
              name="isQnAEnabled"
              valuePropName="checked"
              style={{ flex: "0 0 auto" }}
              extra="Learners can ask questions on this session."
            >
              <Switch checkedChildren="On" unCheckedChildren="Off" />
            </Form.Item>

            <Form.Item
              label="Voting"
              name="isVotingEnabled"
              valuePropName="checked"
              style={{ flex: "0 0 auto" }}
              extra="Learners can vote on this session."
            >
              <Switch checkedChildren="On" unCheckedChildren="Off" />
            </Form.Item>

            <Form.Item
              label="Add to batches"
              name="batchIds"
              style={{ flex: "1 1 320px" }}
              extra="The session will be appended after the existing sessions in each selected batch."
            >
              <Select
                mode="multiple"
                allowClear
                showSearch
                optionFilterProp="label"
                maxTagCount="responsive"
                loading={batchesLoading}
                placeholder={
                  batchesLoading
                    ? "Loading active batches..."
                    : "Select active batches (optional)"
                }
                options={batchOptions}
                dropdownRender={(menu) => (
                  <>
                    <div style={{ padding: "4px 8px" }}>
                      <Checkbox
                        checked={
                          selectableBatchIds.length > 0 &&
                          (form.getFieldValue("batchIds") ?? []).length >=
                            selectableBatchIds.length
                        }
                        indeterminate={
                          (form.getFieldValue("batchIds") ?? []).length > 0 &&
                          (form.getFieldValue("batchIds") ?? []).length <
                            selectableBatchIds.length
                        }
                        disabled={!selectableBatchIds.length}
                        onChange={(e) => {
                          form.setFieldsValue({
                            batchIds: e.target.checked
                              ? selectableBatchIds
                              : [],
                          });
                        }}
                      >
                        Select all batches ({selectableBatchIds.length})
                      </Checkbox>
                    </div>
                    <Divider style={{ margin: "4px 0" }} />
                    {menu}
                  </>
                )}
              />
            </Form.Item>

            <Form.Item
              noStyle
              shouldUpdate={(previous, current) =>
                previous?.batchIds !== current?.batchIds
              }
            >
              {({ getFieldValue }) => {
                const hasSelectedBatches =
                  (getFieldValue("batchIds") ?? []).length > 0;
                return (
                  <Form.Item
                    label="Notify learners via"
                    name="notificationChannels"
                    style={{ flex: "1 1 260px" }}
                    extra={
                      hasSelectedBatches
                        ? "Notifications will be sent when the session is added."
                        : "Select at least one batch to notify learners."
                    }
                  >
                    <Checkbox.Group
                      disabled={!hasSelectedBatches}
                      options={[
                        { label: "WhatsApp", value: "whatsapp" },
                        { label: "Email", value: "email" },
                      ]}
                    />
                  </Form.Item>
                );
              }}
            </Form.Item>
          </Space>

          <Space
            size={16}
            style={{ display: "flex", flexWrap: "wrap", marginTop: 8 }}
            align="start"
          >
            <Form.Item
              label="Video URL"
              name="videoUrl"
              style={{ flex: "1 1 320px" }}
              rules={[{ required: false }]}
            >
              <Input placeholder="e.g. https://vimeo.com/123456" />
            </Form.Item>

            <Form.Item
              label="Zoom Meeting Link"
              name="zoomMeetingLink"
              style={{ flex: "1 1 320px" }}
              rules={[{ required: false }]}
            >
              <Input placeholder="e.g. https://zoom.us/j/123456789" />
            </Form.Item>

            <Form.Item
              label="Description"
              name="description"
              style={{ flex: "1 1 320px" }}
              rules={[{ required: false }]}
            >
              <Input.TextArea rows={3} placeholder="Session description" />
            </Form.Item>
          </Space>

          <Form.List name="notesPdfUrl">
            {(fields, { add, remove }) => (
              <div style={{ marginTop: "1rem" }}>
                <h4 style={{ marginBottom: 12 }}>Notes PDF URLs</h4>
                {fields.map((field, idx) => (
                  <Space
                    key={field.key}
                    align="baseline"
                    style={{ display: "flex", marginBottom: 8 }}
                  >
                    <Form.Item
                      name={[field.name, "enabled"]}
                      valuePropName="checked"
                      style={{ marginBottom: 0 }}
                      initialValue={true}
                    >
                      <Checkbox />
                    </Form.Item>
                    <Form.Item
                      {...field}
                      name={[field.name, "url"]}
                      rules={[
                        {
                          required: false,
                        },
                      ]}
                      label={idx === 0 ? "Notes URL" : ""}
                    >
                      <Input placeholder="https://clapingo-assets.s3.amazonaws.com/notes1.pdf" />
                    </Form.Item>
                    <Button
                      danger
                      onClick={() => remove(field.name)}
                      disabled={fields.length <= 1}
                    >
                      Remove
                    </Button>
                  </Space>
                ))}

                <Button
                  type="dashed"
                  onClick={() => add({ url: "", enabled: true })}
                  style={{ width: "100%" }}
                >
                  Add Notes PDF URL
                </Button>
              </div>
            )}
          </Form.List>

          <div style={{ marginTop: 16, textAlign: "right" }}>
            <Button onClick={() => form.resetFields()} style={{ marginRight: 8 }}>
              Reset
            </Button>
            <Button type="primary" htmlType="submit" loading={submitting}>
              {submitting ? "Saving..." : editingId ? "Update Session" : "Save Session"}
            </Button>
          </div>
        </Form>
      </Card>

      <Card title="Saved AI Past Sessions">
        {loading ? (
          <div className="spin-center">
            <Spin size="large" />
          </div>
        ) : (
          <Table
            dataSource={tableData}
            columns={columns}
            pagination={{
              current: currentPage,
              pageSize: PAGE_SIZE,
              total: totalItems,
              showSizeChanger: false,
              showTotal: (total, range) =>
                total > 0
                  ? `${range[0]}-${range[1]} of ${total} sessions`
                  : "0 sessions",
              onChange: (page) => fetchSessions(page),
            }}
            scroll={{ x: 1780 }}
          />
        )}
      </Card>
    </div>
  );
};

export default AiPastSessions;

